import supabase from "./supabaseClient";

/**
 * ============================================================
 * REAL SUPABASE ADMIN AUTH SERVICE
 * ============================================================
 * Production authentication and authorization service backed
 * strictly by Supabase Auth and the database `public.is_admin()`
 * Row Level Security (RLS) check against `public.admin_users`.
 *
 * No mock credentials or insecure local storage fallbacks.
 * ============================================================
 */

let cachedAdminUser = null;
let isInitialized = false;

// Helper to extract a display name from Supabase user
const formatAdminUser = (user) => {
  if (!user) return null;
  const metadata = user.user_metadata || {};
  return {
    id: user.id,
    email: user.email,
    name: metadata.full_name || metadata.name || user.email?.split("@")[0] || "Administrator",
    role: "Administrator",
    lastSignIn: user.last_sign_in_at,
  };
};

export const adminAuthService = {
  /**
   * Synchronous check if a candidate session token exists.
   * Note: Route guards should also invoke `checkAuth()` asynchronously
   * to guarantee the token is verified against `public.is_admin()`.
   */
  isAuthenticated() {
    if (cachedAdminUser) return true;
    try {
      const raw = localStorage.getItem("shiveda_supabase_auth_token");
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      // Supabase stores currentSession inside the stored token
      return Boolean(parsed?.currentSession?.user);
    } catch {
      return false;
    }
  },

  /**
   * Returns currently verified administrator user object from memory/session.
   */
  getCurrentAdmin() {
    if (cachedAdminUser) return cachedAdminUser;
    try {
      const raw = localStorage.getItem("shiveda_supabase_auth_token");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const user = parsed?.currentSession?.user;
      if (user) {
        cachedAdminUser = formatAdminUser(user);
        return cachedAdminUser;
      }
      return null;
    } catch {
      return null;
    }
  },

  /**
   * Asynchronously validates the current active session against Supabase Auth
   * and verifies admin authorization via `public.is_admin()` RPC.
   *
   * @returns {Promise<Object|null>} Verified admin user or null if unauthenticated / non-admin
   */
  async checkAuth() {
    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session?.user) {
        cachedAdminUser = null;
        isInitialized = true;
        return null;
      }

      // Verify the authenticated user has active admin role in public.admin_users
      const { data: isAdmin, error: rpcError } = await supabase.rpc("is_admin");

      if (rpcError || !isAdmin) {
        console.warn("[AdminAuth] User is authenticated but lacks is_admin() privilege. Signing out.");
        await supabase.auth.signOut();
        cachedAdminUser = null;
        isInitialized = true;
        return null;
      }

      cachedAdminUser = formatAdminUser(session.user);
      isInitialized = true;
      return cachedAdminUser;
    } catch (err) {
      console.error("[AdminAuth] Unexpected error during checkAuth:", err);
      cachedAdminUser = null;
      isInitialized = true;
      return null;
    }
  },

  /**
   * Authenticates administrator using Supabase Auth email and password,
   * then verifies admin authorization via database `public.is_admin()`.
   *
   * @param {Object} credentials
   * @param {string} credentials.email - Administrator email
   * @param {string} credentials.password - Administrator password
   * @returns {Promise<Object>}
   */
  async login({ email, password }) {
    const cleanEmail = (email || "").trim().toLowerCase();
    const cleanPassword = (password || "").trim();

    if (!cleanEmail || !cleanPassword) {
      throw new Error("Please provide both administrator email and password.");
    }

    // 1. Authenticate with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: cleanEmail,
      password: cleanPassword,
    });

    if (authError || !authData?.user) {
      throw new Error(authError?.message || "Invalid administrator credentials.");
    }

    // 2. Authorize via public.is_admin() database function
    const { data: isAdmin, error: rpcError } = await supabase.rpc("is_admin");

    if (rpcError) {
      await supabase.auth.signOut();
      throw new Error(`Authorization check failed: ${rpcError.message}`);
    }

    if (!isAdmin) {
      // User is an authenticated user, but not an authorized administrator in public.admin_users
      await supabase.auth.signOut();
      throw new Error("Access denied: Your account does not have administrator privileges.");
    }

    cachedAdminUser = formatAdminUser(authData.user);

    return {
      success: true,
      user: cachedAdminUser,
      message: "Administrator session established successfully.",
    };
  },

  /**
   * Signs out the current administrator from Supabase Auth.
   */
  async logout() {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn("[AdminAuth] Sign out warning:", err);
    } finally {
      cachedAdminUser = null;
    }
    return { success: true };
  },

  /**
   * Subscribes to auth state changes to keep admin session synchronized.
   */
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_OUT" || !session) {
        cachedAdminUser = null;
        callback(null);
      } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        const { data: isAdmin } = await supabase.rpc("is_admin");
        if (isAdmin) {
          cachedAdminUser = formatAdminUser(session.user);
          callback(cachedAdminUser);
        } else {
          cachedAdminUser = null;
          callback(null);
        }
      }
    });
  },
};

export default adminAuthService;
